import React from 'react'
import { Link } from 'react-router-dom'
import {
  Shield,
  Zap,
  Users,
  CheckCircle,
  ArrowRight,
  Play,
  Star,
  TrendingUp,
  Lock,
  Clock,
  BarChart3,
  Building2,
  Stethoscope
} from 'lucide-react'
import { AnimatedLogo, RippleCard } from '../../components/healthfluence'

const stats = [
  { label: 'Healthcare Creators', value: '10,000+' },
  { label: 'Campaigns Completed', value: '2,500+' },
  { label: 'Total Reach', value: '500M+' },
  { label: 'Client Satisfaction', value: '98%' },
]

const features = [
  {
    icon: Shield,
    title: 'Compliance-First',
    description: 'FDA, FTC, and HIPAA compliance built into every workflow. Reduce legal risk with automated checks.',
  },
  {
    icon: Users,
    title: 'Vetted Creators',
    description: 'Access a curated network of verified healthcare professionals, patient advocates, and health educators.',
  },
  {
    icon: Zap,
    title: 'AI-Powered Matching',
    description: 'Our intelligent matching engine connects you with the perfect creators in under 48 hours.',
  },
  {
    icon: Lock,
    title: 'Escrow Payments',
    description: 'Secure milestone-based payments protect both brands and creators throughout the campaign.',
  },
  {
    icon: BarChart3,
    title: 'Real-Time Analytics',
    description: 'Track impressions, engagement, conversions, and ROI with comprehensive campaign dashboards.',
  },
  {
    icon: Clock,
    title: 'Managed Workflows',
    description: 'From brief to delivery, our platform mediates all interactions for consistent outcomes.',
  },
]

const testimonials = [
  {
    quote: "Healthfluence transformed how we approach influencer marketing. The compliance automation alone saved us countless hours of legal review.",
    author: 'Sarah Chen',
    role: 'VP Marketing',
    company: 'MedDevice Corp',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
  },
  {
    quote: "Finally, a platform that understands healthcare. The vetted creator network gives us confidence in every partnership.",
    author: 'Dr. Michael Torres',
    role: 'CMO',
    company: 'Digital Health Startup',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
  },
  {
    quote: "As a creator, Healthfluence provides fair compensation, clear briefs, and brands I'm proud to work with. It's changed my career.",
    author: 'Dr. Emily Watson',
    role: 'Health Educator',
    company: '500K+ Followers',
    avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&h=100&fit=crop',
  },
]

function LandingPage() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-hf-teal-50 via-white to-hf-teal-100/30">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-40">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-1.5 bg-hf-teal-100 text-hf-teal-700 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4 mr-2" />
                Compliance-First Healthcare Marketing
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                The Trusted Marketplace for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-hf-teal-600 to-hf-navy-700">
                  Healthcare Influence
                </span>
              </h1>
              <p className="mt-6 text-xl text-gray-600 max-w-xl mx-auto lg:mx-0">
                Connect with vetted medical influencers through compliant workflows.
                Launch campaigns that educate, engage, and inspire healthier outcomes.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/register?type=brand" className="btn-primary text-lg px-8 py-3">
                  Start as a Brand
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link to="/register?type=creator" className="btn-secondary text-lg px-8 py-3">
                  Join as a Creator
                </Link>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                Trusted by 500+ healthcare brands and 10,000+ verified creators
              </p>
            </div>

            {/* Hero Image/Illustration */}
            <div className="mt-16 lg:mt-0 relative">
              <div className="relative mx-auto max-w-lg">
                {/* Dashboard Preview Card */}
                <RippleCard className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 transform rotate-1">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-gray-500">Campaign Performance</p>
                      <p className="text-2xl font-bold text-gray-900">$1.2M Reach</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-gray-900">24</p>
                      <p className="text-xs text-gray-500">Creators</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-gray-900">4.8%</p>
                      <p className="text-xs text-gray-500">Engagement</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-gray-900">100%</p>
                      <p className="text-xs text-gray-500">Compliant</p>
                    </div>
                  </div>
                </RippleCard>

                {/* Floating Creator Card */}
                <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl border border-gray-100 p-4 transform -rotate-3">
                  <div className="flex items-center space-x-3">
                    <img
                      src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=60&h=60&fit=crop"
                      alt="Creator"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="flex items-center">
                        <p className="font-semibold text-gray-900">Dr. Sarah K.</p>
                        <CheckCircle className="w-4 h-4 text-hf-teal-600 ml-1" />
                      </div>
                      <p className="text-xs text-gray-500">250K Followers • Neurology</p>
                    </div>
                  </div>
                </div>

                {/* Compliance Badge */}
                <div className="absolute -top-4 -right-4 bg-hf-teal-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium flex items-center shadow-lg">
                  <Shield className="w-4 h-4 mr-1.5" />
                  Verified
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-hf-teal-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-white">{stat.value}</p>
                <p className="mt-1 text-hf-teal-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everything You Need for Healthcare Influence
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Healthfluence is the operating system that makes healthcare influencer marketing
              scalable, compliant, and measurable.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <RippleCard key={feature.title} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-hf-teal-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-hf-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </RippleCard>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Launch Campaigns in 3 Simple Steps
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              From brief to results in weeks, not months.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Submit Your Brief',
                description: 'Use our guided brief builder to capture your campaign objectives, compliance requirements, and creative direction.',
                icon: Building2,
              },
              {
                step: '02',
                title: 'Review Matched Creators',
                description: 'Our AI matching engine curates a shortlist of vetted creators perfectly aligned with your brand and audience.',
                icon: Users,
              },
              {
                step: '03',
                title: 'Launch & Track',
                description: 'Approve content through compliant workflows and track real-time performance across all metrics.',
                icon: TrendingUp,
              },
            ].map((item) => (
              <div key={item.step} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full">
                  <span className="text-5xl font-bold text-hf-teal-100">{item.step}</span>
                  <div className="mt-4">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Brands / For Creators Split */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* For Brands */}
            <div id="for-brands" className="bg-gradient-to-br from-hf-teal-50 to-hf-teal-100/50 rounded-3xl p-8 lg:p-12">
              <div className="w-14 h-14 bg-hf-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Healthcare Brands</h3>
              <p className="text-gray-600 mb-6">
                MedTech, Pharma, Digital Health, and Wellness brands trust Healthfluence to
                connect with authentic voices in healthcare.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Curated, vetted healthcare influencers',
                  'Compliance-first workflows (FDA/FTC/HIPAA)',
                  'Campaign launch in weeks, not months',
                  'Transparent analytics and benchmarks',
                  'Scale from 1 to 100+ influencers',
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-hf-teal-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register?type=brand" className="btn-primary">
                Apply as a Brand
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>

            {/* For Creators */}
            <div id="for-creators" className="bg-gradient-to-br from-healthcare-teal/10 to-healthcare-mint/10 rounded-3xl p-8 lg:p-12">
              <div className="w-14 h-14 bg-healthcare-teal rounded-2xl flex items-center justify-center mb-6">
                <Stethoscope className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Healthcare Creators</h3>
              <p className="text-gray-600 mb-6">
                Physicians, nurses, patient advocates, and health educators join Healthfluence
                to build sustainable careers sharing evidence-based information.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Fair, transparent compensation',
                  'Clear briefs with defined revision limits',
                  'Built-in compliance support',
                  'Professional growth tools and analytics',
                  'Access to premium brand partnerships',
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healthcare-teal mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register?type=creator" className="inline-flex items-center justify-center px-4 py-2 bg-healthcare-teal text-white font-medium rounded-lg hover:bg-teal-700 transition-colors">
                Join as a Creator
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Trusted by Industry Leaders
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Hear from brands and creators transforming healthcare marketing with Healthfluence.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <RippleCard key={testimonial.author} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="ml-3">
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </RippleCard>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-hf-teal-600 to-hf-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Healthcare Marketing?
          </h2>
          <p className="text-xl text-hf-teal-100 mb-10">
            Join 500+ brands and 10,000+ creators building trust in healthcare influence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-hf-teal-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-lg"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a
              href="#"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors text-lg"
            >
              <Play className="w-5 h-5 mr-2" />
              Watch Demo
            </a>
          </div>
          <p className="mt-6 text-hf-teal-200 text-sm">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  )
}

export default LandingPage
